package com.elan.edubridge.ui.shell

import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.SessionState
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import kotlin.js.Date

/**
 * 셸 화면들이 공유하는 최소 유틸리티. 라우트별 렌더 함수가 반복하는
 * "root 조회 → className 지정 → innerHTML 대입" 3줄만 모았다 — 그 이상의 프레임워크는 없다
 * (프로젝트가 kotlinx-html 등 DSL 의존성을 쓰지 않고 문자열 템플릿 + 직접 DOM 조작을 쓰는
 * 기존 Main.kt 관례를 그대로 따른다).
 */

/** index.html의 유일한 마운트 지점. */
fun appRoot(): HTMLElement = document.getElementById("app-root") as HTMLElement

/** 페이지 전체를 교체한다. rootClass는 가이드가 각 화면의 최상위 요소에 지정한 BEM 블록 클래스다. */
fun renderPage(rootClass: String, html: String) {
    val root = appRoot()
    root.className = rootClass
    root.innerHTML = html
}

/** icons.svg 스프라이트 참조 헬퍼(가이드 §2.3). 장식용 기본값(aria-hidden) — 아이콘 단독으로 의미를 전달하는
 * 버튼은 호출부가 버튼 자체에 aria-label을 달아야 한다(§6.3). */
fun icon(id: String, size: String = "md", extraClass: String = ""): String =
    """<svg class="eb-icon eb-icon--$size $extraClass" aria-hidden="true"><use href="/icons.svg#$id"></use></svg>"""

/**
 * 랜딩(§3.1)·이용 가이드 같은 공개 페이지의 공통 푸터. 사업자 표시는 법적 고지 페이지와 같은 상수를 쓴다 —
 * 전자상거래법 §13 표시 항목(상호·대표자·사업자등록번호·통신판매업 신고번호·주소·연락처)이 랜딩에서만
 * `[대괄호]` 자리표시자로 남아 있던 것을 실제 값으로 잇는다. 스토어 배지는 [StoreLinks] 쪽이다.
 */
fun publicFooter(): String = """
    <footer class="eb-landing-footer">
        <nav aria-label="법적 고지">
            <a href="/terms">이용약관</a><a href="/privacy">개인정보처리방침</a>
            <a href="/support">고객지원</a><a href="/account-deletion">계정 삭제 안내</a>
        </nav>
        <p class="eb-meta">
            ${LegalPages.COMPANY} · 대표 ${LegalPages.REPRESENTATIVE} · 사업자등록번호 ${LegalPages.BUSINESS_NUMBER} · 통신판매업 신고번호 ${LegalPages.MAIL_ORDER_NUMBER}<br>
            ${LegalPages.ADDRESS} · <a href="mailto:${LegalPages.SUPPORT_EMAIL}">${LegalPages.SUPPORT_EMAIL}</a>
        </p>
    </footer>
""".trimIndent()

fun String.escapeHtml(): String = this
    .replace("&", "&amp;")
    .replace("<", "&lt;")
    .replace(">", "&gt;")
    .replace("\"", "&quot;")
    .replace("'", "&#39;")

fun SessionState.accountName(): String = account?.name.orEmpty()

/**
 * 공지 본문. 서식 공지([contentHtml])는 서버가 살균해 둔 HTML 이라 그대로 넣고, 평문 공지는 이스케이프해
 * 줄바꿈만 살린다. 클라이언트는 살균하지 않는다 — 살균은 `functions/src/common/html.ts` 한 곳이다.
 */
fun noticeBodyHtml(contentHtml: String?, content: String): String =
    if (contentHtml != null) {
        """<div class="eb-richtext">$contentHtml</div>"""
    } else {
        """<div style="white-space: pre-wrap;">${content.escapeHtml()}</div>"""
    }

/** 에디터가 생기기 전에 쓴 평문 공지를 에디터에 올릴 때 — 빈 줄은 문단, 줄바꿈은 `<br>`(안드로이드와 같은 규칙). */
fun plainTextToHtml(text: String): String = text
    .escapeHtml()
    .split(Regex("\\n\\s*\\n"))
    .filter { it.isNotBlank() }
    .joinToString("") { paragraph -> "<p>" + paragraph.trim().replace("\n", "<br>") + "</p>" }

/* ---------- 읽기 실패 표시 (#30) ---------- */
//
// 빈 상태와 **다른 화면**이어야 한다. 같은 문구를 쓰면 사용자는 "없구나"로 읽고 떠나며,
// 그게 정확히 #30이 고치려는 결함이다. 화면마다 문구를 새로 지으면 다시 갈라지므로 여기 한 벌만 둔다.

/**
 * 실패 원인별 안내. **재시도로 풀리는 실패에만 재시도를 권한다** — 권한 없음에 "다시 시도"를
 * 붙이면 누를 때마다 같은 화면이 나오고 사용자는 자기가 뭘 잘못했는지 찾게 된다.
 */
private fun readFailureCopy(failure: CommandFailure): Pair<String, Boolean> = when (failure) {
    CommandFailure.PERMISSION_DENIED ->
        "이 목록을 볼 권한이 없어요. 학원 관리자에게 문의해 주세요." to false
    CommandFailure.UNAUTHENTICATED ->
        "로그인이 만료됐어요. 다시 로그인한 뒤 확인해 주세요." to false
    CommandFailure.NOT_FOUND ->
        "찾을 수 없어요. 주소가 바뀌었거나 삭제됐을 수 있어요." to false
    CommandFailure.UNAVAILABLE ->
        "연결이 불안정해요. 잠시 후 다시 시도해 주세요." to true
    else ->
        "정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요." to true
}

/**
 * 목록 전체를 못 읽었을 때. 빈 상태 자리에 **대신** 들어간다.
 *
 * [retryId]가 null이 아니면 그 id로 "다시 시도" 버튼을 그린다 — 호출부가 그 id에 리스너를
 * 붙여 자기 로드 함수를 다시 부른다.
 */
fun readErrorHtml(failure: CommandFailure, retryId: String?): String {
    val (message, retryable) = readFailureCopy(failure)
    val retry = if (retryable && retryId != null) {
        """<button class="eb-button eb-button--primary" id="${retryId.escapeHtml()}" type="button">다시 시도</button>"""
    } else {
        ""
    }
    return """
        <div class="eb-empty-state" role="alert">
            ${icon("icon-warning", "xl")}
            <h2>불러오지 못했어요</h2>
            <p>${message.escapeHtml()}</p>
            $retry
        </div>
    """.trimIndent()
}

/**
 * 일부만 못 읽었을 때. 목록은 그대로 그리고 그 위에 얹는다 —
 * 학원 여러 곳을 병렬로 읽는 화면에서 하나가 빠진 것을 숨기지 않기 위한 것이다.
 */
fun partialFailureHtml(retryId: String?): String {
    val retry = if (retryId != null) {
        """<button class="eb-button eb-button--ghost eb-button--sm" id="${retryId.escapeHtml()}" type="button">다시 시도</button>"""
    } else {
        ""
    }
    return """
        <p class="eb-form-error" role="alert">일부를 불러오지 못했어요. 목록이 완전하지 않을 수 있어요. $retry</p>
    """.trimIndent()
}

/**
 * [readErrorHtml]·[partialFailureHtml]이 그린 "다시 시도"에 동작을 붙인다.
 * 버튼이 없는 경우(재시도로 안 풀리는 실패)에도 안전하게 아무 일도 하지 않는다.
 */
fun wireRetry(retryId: String, onRetry: () -> Unit) {
    (document.getElementById(retryId) as? HTMLElement)?.onclick = {
        onRetry()
        null
    }
}

/* ---------- 날짜·시각 표기 정본 (design-audit §7 · A4) ---------- */
//
// 시간대는 **학원 기준 KST(+09:00, DST 없음) 고정**이다(§4-9 제품 결정, 2026-08-13 확정).
// 브라우저 로컬을 쓰면 해외 체류 보호자에게 "새벽 3시 등원"이 표시된다 — 출결·공지는 학원에서
// 일어난 사건이라 학원의 시계로 읽어야 한다. 학원 `timeZone` 필드가 KST 외 값을 갖게 되는 날
// 이 상수를 그 값으로 대체하는 것이 후속 작업의 전부가 되도록 계산을 여기 한 곳에 모은다.

internal const val KST_OFFSET_MS = 9 * 60 * 60 * 1000

/**
 * ISO(UTC) 순간의 KST 필드 뷰. 반환된 Date는 **UTC 게터로만** 읽어야 한다 —
 * 9시간을 더해 두었으므로 `getUTC*`가 곧 KST 값이다. 파싱 실패는 null.
 */
private fun kstView(iso: String): Date? {
    if (iso.isBlank()) return null
    val date = Date(iso)
    if (date.toString() == "Invalid Date") return null
    return Date(date.getTime() + KST_OFFSET_MS)
}

private fun kstNow(): Date = Date(Date().getTime() + KST_OFFSET_MS)

private fun dayKey(view: Date): Int =
    view.getUTCFullYear() * 10_000 + view.getUTCMonth() * 100 + view.getUTCDate()

/** "오후 4:40". 오전/오후는 필수다 — 등·하원의 6시가 아침인지 저녁인지는 결함 급 차이다(§7.2). */
private fun clockLabel(view: Date): String {
    val hours = view.getUTCHours()
    val period = if (hours < 12) "오전" else "오후"
    val hour12 = if (hours % 12 == 0) 12 else hours % 12
    return "$period $hour12:${view.getUTCMinutes().toString().padStart(2, '0')}"
}

/**
 * 목록 행·상세 메타 정본(§7.1). 오늘·어제만 상대 표기, 올해는 연도 생략, 지난해부터는 시각을
 * 뗀다("1년 전 글이 몇 시에 올라왔는지는 의사결정에 쓰이지 않는다"). 파싱 실패는 원문 폴백.
 *
 * 이 함수가 기존 5중 복제(`formatTimestamp` A안 KST 하드코딩 / B안 브라우저 로컬·시각 소실)를
 * 대체하는 유일본이다 — 페이지에 다시 사본을 만들지 말 것.
 */
fun formatTimestamp(iso: String): String {
    val view = kstView(iso) ?: return if (iso.isBlank()) "날짜 미상" else iso.escapeHtml()
    val now = kstNow()
    val yesterday = Date(now.getTime() - 24 * 60 * 60 * 1000)
    return when {
        dayKey(view) == dayKey(now) -> "오늘 ${clockLabel(view)}"
        dayKey(view) == dayKey(yesterday) -> "어제 ${clockLabel(view)}"
        view.getUTCFullYear() == now.getUTCFullYear() ->
            "${view.getUTCMonth() + 1}월 ${view.getUTCDate()}일 ${clockLabel(view)}"
        else -> "${view.getUTCFullYear()}년 ${view.getUTCMonth() + 1}월 ${view.getUTCDate()}일"
    }
}

/** 타임라인 스텝·실시간 스캔 정본(§7.1): `a h:mm`. */
fun formatKstClockTime(iso: String): String = kstView(iso)?.let(::clockLabel) ?: iso

/** 절대 시각 한 줄("8월 14일 오후 4:40"). 재신청 쿨다운처럼 미래 시각 안내에도 쓴다. */
fun formatKstDateTime(iso: String): String {
    val view = kstView(iso) ?: return iso
    val year = if (view.getUTCFullYear() == kstNow().getUTCFullYear()) "" else "${view.getUTCFullYear()}년 "
    return "$year${view.getUTCMonth() + 1}월 ${view.getUTCDate()}일 ${clockLabel(view)}"
}

/**
 * 최신 세션 스냅샷. Main.kt가 세션 스트림을 구독하며 매 방출마다 갱신하고,
 * 라우트 핸들러는 디스패치 시점에 이 값을 읽어 항상 최신 상태로 렌더한다
 * (핸들러마다 세션을 매개변수로 threading하지 않기 위한 최소 장치).
 */
object SessionStore {
    var current: SessionState = SessionState()
        internal set
}
